import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Input } from "antd";
import {
	Building2,
	MapPin,
	CheckCircle2,
	AlertTriangle,
	Clock,
	Loader2,
	FileSearch,
	FolderOpen,
	ChevronRight,
	Search
} from "lucide-react";

import DashboardEmptyState from "components/dashboard-empty-state/dashboard-empty-state";
import SubmissionTypeBadge from "components/submission-type-badge/submission-type-badge";
import routeNames from "constants/route-names";
import { getPropertyAddress } from "utils/helpers";
import { PlanReviewJobStatus, PlanReviewReviewOutcome } from "constants/field-values";
import { useGetFilteredPlanReviewsQuery } from "graphql/queries/get-filtered-plan-reviews.generated";

type PlanReviewStatus = "analyzing" | "approved" | "revisions" | "failed";

const statusConfig: Record<PlanReviewStatus, { label: string; icon: typeof CheckCircle2; badgeClass: string }> = {
	analyzing: { label: "Analyzing", icon: Loader2, badgeClass: "id-status-review" },
	approved: { label: "Approved", icon: CheckCircle2, badgeClass: "id-status-fast-lane" },
	revisions: { label: "Revisions Needed", icon: AlertTriangle, badgeClass: "id-status-review" },
	// A failed run is the analysis breaking down, not a verdict on the package: its own
	// wording, so it is never read as a rejection.
	failed: { label: "Oops! Laid an Egg", icon: AlertTriangle, badgeClass: "id-status-not-ready" }
};

// Derive the list-card status from the latest submission's state + outcome.
const deriveStatus = (state?: string | null, outcome?: string | null): PlanReviewStatus => {
	if (state === PlanReviewJobStatus.FAILED) return "failed";
	if (state !== PlanReviewJobStatus.COMPLETE) return "analyzing";
	if (outcome === PlanReviewReviewOutcome.APPROVED) return "approved";
	return "revisions";
};

const statItems = [
	{ id: "all", label: "Total", cardClass: "id-stat-card-total", icon: FileSearch },
	{ id: "approved", label: "Approved", cardClass: "id-stat-card-fast-lane", icon: CheckCircle2 },
	{ id: "revisions", label: "Revisions Needed", cardClass: "id-stat-card-review", icon: AlertTriangle },
	{ id: "analyzing", label: "In Review", cardClass: "prl-stat-card-in-review", icon: Clock }
] as const;

const filterTabs = [
	{ id: "all", label: "All" },
	{ id: "approved", label: "Approved" },
	{ id: "revisions", label: "Revisions Needed" },
	{ id: "analyzing", label: "In Review" }
] as const;

const formatReviewType = (raw?: string | null): string =>
	(raw ?? "fire_plan_review").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

const PlanReviewsList = () => {
	const [searchTerm, setSearchTerm] = useState("");
	const [statusFilter, setStatusFilter] = useState<string>("all");

	// Row-level permissions filter plan_reviews by the session vars, so an empty
	// `where` returns only the projects the logged-in user is allowed to see.
	const { data, loading, error } = useGetFilteredPlanReviewsQuery({
		variables: { where: {} },
		fetchPolicy: "cache-and-network"
	});

	const reviews = useMemo(() => {
		return (data?.plan_reviews ?? []).map(planReview => {
			// The query asks for the submissions ordered by submission_number desc with
			// limit 1, so this is the most recent one. A project always has at least one,
			// but the card degrades to "Analyzing" if a submission is still being created.
			const latest = planReview.plan_review_jobs[0];
			return {
				id: planReview.id,
				ollieId: planReview.ollie_id,
				name: planReview.property?.name ?? "Untitled property",
				address: getPropertyAddress(planReview.property?.address, planReview.property?.standardized_address) || "—",
				ahj: planReview.jurisdiction_record?.name ?? formatReviewType(planReview.review_type),
				submissionType: planReview.submission_type ?? null,
				photoUrl: planReview.property?.photo_link ?? undefined,
				status: deriveStatus(latest?.state, latest?.review_outcome),
				score: latest?.compliance_score ?? null,
				// Only what needs attention: the query filters the aggregate to the flagged
				// statuses, so this is the same number the submittal report reports.
				findings: latest?.plan_review_findings_aggregate.aggregate?.count ?? 0,
				submissions: planReview.plan_review_jobs_aggregate.aggregate?.count ?? 0
			};
		});
	}, [data]);

	const filtered = useMemo(() => {
		const term = searchTerm.toLowerCase();
		return reviews.filter(r => {
			const matchesSearch =
				r.name.toLowerCase().includes(term) ||
				r.address.toLowerCase().includes(term) ||
				r.ollieId.toLowerCase().includes(term);
			const matchesStatus = statusFilter === "all" || r.status === statusFilter;
			return matchesSearch && matchesStatus;
		});
	}, [reviews, searchTerm, statusFilter]);

	const statValues = [
		reviews.length,
		reviews.filter(r => r.status === "approved").length,
		reviews.filter(r => r.status === "revisions").length,
		reviews.filter(r => r.status === "analyzing").length
	];

	const hasNoPlanReviews = !error && reviews.length === 0;

	return (
		<>
			<div className="id-stats-grid id-animate" style={{ animationDelay: "100ms" }}>
				{statItems.map((stat, i) => {
					const Icon = stat.icon;
					return (
						<div key={stat.label} className={`id-stat-card ${stat.cardClass}`} onClick={() => setStatusFilter(stat.id)}>
							<div className="id-stat-card-header">
								<span className="id-stat-label">{stat.label}</span>
								<Icon className="id-stat-icon" />
							</div>
							<p className="id-stat-value">{statValues[i]}</p>
						</div>
					);
				})}
			</div>

			<div className="id-filter-bar id-animate" style={{ animationDelay: "200ms" }}>
				<div className="id-search-wrap">
					<Input
						placeholder="Search properties..."
						value={searchTerm}
						onChange={e => setSearchTerm(e.target.value)}
						prefix={<Search className="id-search-icon" />}
						variant="borderless"
						size="large"
						className="id-search-input"
					/>
				</div>
				<div className="id-filter-tabs">
					{filterTabs.map(tab => (
						<button
							key={tab.id}
							onClick={() => setStatusFilter(tab.id)}
							className={`id-filter-tab${statusFilter === tab.id ? "-active" : ""}`}
						>
							{tab.label}
						</button>
					))}
				</div>
			</div>

			<div className="id-property-list">
				{loading && reviews.length === 0 && (
					<div className="id-empty-state id-animate">
						<Loader2 className="rr-loading-spinner" />
						<p>Loading plan reviews…</p>
					</div>
				)}

				{error && (
					<div className="id-empty-state id-animate">
						<p>We couldn&apos;t load your plan reviews. {error.message}</p>
					</div>
				)}

				{!loading &&
					filtered.length === 0 &&
					(hasNoPlanReviews ? (
						<DashboardEmptyState
							tone="plan-review"
							icon={FolderOpen}
							title="No Plan Reviews Yet"
							description="Start by submitting your first set of plans. Ollie will analyze them and help you ensure everything is ready for review."
							actionLabel="New Plan Submittal"
							actionRoute={routeNames.PLAN_REVIEW_ADD}
						/>
					) : (
						<div className="id-empty-state id-animate" style={{ animationDelay: "300ms" }}>
							<p>No plan reviews match your criteria.</p>
						</div>
					))}

				{filtered.map((review, index) => {
					const config = statusConfig[review.status];
					const StatusIcon = config.icon;
					const isApproved = review.status === "approved";
					// A run still going, or one that never finished, has no figures to show —
					// the same dash the site inspections use when there is no Ollie score.
					const score = review.status === "analyzing" || review.status === "failed" ? null : review.score;
					// The dashboard pill says "Revisions" instead of "Revisions Needed". The filter tabs and stat cards keep the full label.
					const statusLabel = review.status === "revisions" ? "Revisions" : config.label;
					return (
						<div
							key={review.id}
							className="id-property-item id-animate"
							style={{ animationDelay: `${300 + index * 80}ms` }}
						>
							<Link
								to={routeNames.PLAN_REVIEW_SUBMISSIONS.replace(":planReviewId", review.id)}
								className={`id-property-card${isApproved ? "-approved" : ""}`}
							>
								{isApproved && <div className="id-approved-glow" />}

								<div className="id-property-card-inner">
									<div className="id-left-section">
										{review.photoUrl ? (
											<img
												src={review.photoUrl}
												alt={review.name}
												className="id-property-img"
												onError={e => {
													(e.target as HTMLImageElement).style.display = "none";
												}}
											/>
										) : (
											<div className="id-property-icon">
												<Building2 className="id-building-icon" />
											</div>
										)}
										<div className="id-property-info">
											<h3 className="id-property-name prl-property-name-above-ollie-id">{review.name}</h3>
											<span className="prl-ollie-id">{review.ollieId}</span>
											<div className="id-property-meta">
												<span className="id-meta-item">
													<MapPin className="id-meta-icon" />
													<span className="id-meta-text">{review.address}</span>
												</span>
												<span className="prl-ahj-row">
													<span className="prl-ahj">
														<Building2 className="prl-ahj-icon" />
														<span className="prl-ahj-name">{review.ahj}</span>
													</span>
													<SubmissionTypeBadge submissionType={review.submissionType} />
												</span>
											</div>
										</div>
									</div>

									<div className="id-property-metrics">
										<div className="id-metric">
											<span className="id-metric-label">Status</span>
											<div className={`id-status-badge ${config.badgeClass}`}>
												<StatusIcon className="id-status-badge-icon" />
												{statusLabel}
											</div>
										</div>
										<div className="id-metric">
											<span className="id-metric-label">Completeness</span>
											<span
												className={`id-metric-figure${score !== null && score >= 50 ? " id-metric-figure-good" : ""}`}
											>
												{score !== null ? `${score}%` : "—"}
											</span>
										</div>
										<div className="id-metric">
											<span className="id-metric-label">Issues</span>
											<span className={`id-metric-figure${review.findings > 0 ? " id-metric-figure-open" : ""}`}>
												{score !== null ? review.findings : "—"}
											</span>
										</div>
										<div className="id-metric">
											<span className="id-metric-label">Submittals</span>
											<span className="id-metric-count">{review.submissions}</span>
										</div>
									</div>

									<div className="id-chevron-wrap">
										<ChevronRight className="id-chevron-icon" />
									</div>
								</div>
							</Link>
						</div>
					);
				})}
			</div>
		</>
	);
};

export default PlanReviewsList;
